/**
 * Post-checkout landing page (route /welcome, public/pre-auth).
 *
 * Stripe's `success_url` for the card-gated trial points here (see
 * DockPro-Backend's registerHandler.ts, createCardGatedCheckoutSession).
 * The visitor has just submitted a card and their 30-day trial has
 * started — but they are NOT signed in and, in almost every case, cannot
 * sign in yet: the email-verification gate (preAuthEmailVerifiedGate)
 * still blocks login until they click the link in the welcome email.
 *
 * So this page's only job is to close the loop honestly: confirm the card
 * landed, say the trial has started, and point at the one thing standing
 * between them and the portal. It deliberately does NOT:
 *
 *   - poll or fetch anything. Activation is driven by Stripe's
 *     `checkout.session.completed` webhook, which is asynchronous and may
 *     land a moment after the redirect. A status check here would race it
 *     and could tell a paying customer their account is inactive.
 *   - claim the account is ready to use. Email verification is a separate
 *     gate this page has no visibility into.
 *
 * Not wrapped in <ProtectedRoute> — same public-route family as /signup,
 * /login and /verify-email, and for the same reason: there is no session.
 */
import { Link, useSearchParams } from "react-router-dom";
import { StatusMessage } from "../components/StatusMessage";
import { BrandHeader } from "../components/BrandHeader";
import styles from "./WelcomePage.module.css";

export function WelcomePage() {
  const [searchParams] = useSearchParams();
  // Present in Stripe's success_url. Shown only as a confirmation detail —
  // nothing on this page is fetched or gated by it, so a missing or
  // tampered value degrades to "no detail line", never to a broken page.
  const tenantId = searchParams.get("tenantId");

  return (
    <main className={styles.pageContainer}>
      <div className={styles.card}>
        <BrandHeader />

        <div className={styles.body}>
          <h1 tabIndex={-1} autoFocus>Trial started</h1>
          <StatusMessage
            variant="status"
            message="Your card is saved and your 30-day free trial has started. You won't be charged until it ends."
            className={styles.success}
          />
          <ol className={styles.steps}>
            <li className={styles.stepDone}>
              <span className={styles.stepMark} aria-hidden="true">✓</span>
              <span>Payment method saved</span>
            </li>
            <li>
              <span className={styles.stepMark} aria-hidden="true">2</span>
              <span>
                Open the welcome email we just sent and click its verification link.
                You&rsquo;ll need it before you can sign in.
              </span>
            </li>
            <li>
              <span className={styles.stepMark} aria-hidden="true">3</span>
              <span>Sign in with the temporary password from that same email.</span>
            </li>
          </ol>
          <p className={styles.subtle}>
            Didn&rsquo;t get the email? Check your spam folder. You can close this page — the
            email arrives either way.
          </p>
          {tenantId && (
            <p className={styles.subtle}>
              Account: <strong>{tenantId}</strong>
            </p>
          )}
          <Link to="/login" className={styles.textLink}>Go to sign in</Link>
        </div>
      </div>
    </main>
  );
}
